import React, { useEffect } from "react";
import { AxiosInstance } from "axios";
import { NavigateFunction } from "react-router";
import { getQuizQuestion } from "src/api/chat";
import { formatMessage } from "src/helpers/format-message";
import { Message, MessageSender } from "./types";

interface UseInitialGreetingOptions {
  configLoaded: boolean;
  suborgConfigLoaded: boolean;
  orgSlug: string;
  subOrgSlug: string;
  quizId: string;
  queryType: string | null | undefined;
  queryTypeFromConfig: string | null | undefined;
  botGreetingMsg: string | null | undefined;
  messagesLength: number;
  api: AxiosInstance;
  navigate: NavigateFunction;
  queryTypeRef: React.MutableRefObject<string>;
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  onQuizNotFound: () => void;
}

// First-paint effect for the chat: once the org config is loaded (and the
// suborg config too if applicable), if no messages have been rendered yet,
// either fetch the first quiz question (QUIZ mode) or append the configured
// greeting (everything else). Direct port of the inline effect from Chat.tsx.
export const useInitialGreeting = ({
  configLoaded,
  suborgConfigLoaded,
  orgSlug,
  subOrgSlug,
  quizId,
  queryType,
  queryTypeFromConfig,
  botGreetingMsg,
  messagesLength,
  api,
  navigate,
  queryTypeRef,
  setMessages,
  onQuizNotFound,
}: UseInitialGreetingOptions) => {
  useEffect(() => {
    if (
      configLoaded &&
      (subOrgSlug ? suborgConfigLoaded : true) &&
      messagesLength === 0
    ) {
      // If quiz_id exists, query type is already set to 'QUIZ' by the previous
      // useEffect. Otherwise fall back to queryType or queryTypeFromConfig.
      const type = quizId ? "QUIZ" : queryType || queryTypeFromConfig;

      if (type === "QUIZ") {
        queryTypeRef.current = "QUIZ";
        const fetchQuizQuestion = async () => {
          try {
            const currentSubOrgSlug = subOrgSlug || "";

            setMessages((prevMessages) => [
              ...prevMessages,
              {
                id: prevMessages.length + 1,
                message: "Processing...",
                sender: MessageSender.Bot,
                isProcessing: true,
                docs: [],
                imagesLoading: false,
              },
            ]);

            const response = await getQuizQuestion(
              api,
              currentSubOrgSlug.length > 0 ? currentSubOrgSlug : orgSlug,
              quizId || undefined,
            );

            if (response && response.quiz_id && response.quiz_id !== quizId) {
              if (subOrgSlug) {
                navigate(
                  `/${orgSlug}/quiz/${response.quiz_id}/${subOrgSlug}`,
                  { replace: true },
                );
              } else {
                navigate(`/${orgSlug}/quiz/${response.quiz_id}`, {
                  replace: true,
                });
              }
            }

            if (response && response.quiz_question) {
              setMessages((prevMessages) => {
                const lastMessage = prevMessages[prevMessages.length - 1];
                if (lastMessage.sender === "bot") {
                  lastMessage.message = formatMessage(response.quiz_question);
                  lastMessage.isProcessing = false;
                }
                return [...prevMessages];
              });
            }
          } catch (error) {
            console.error("Error fetching quiz question:", error);
            throw error;
          }
        };

        fetchQuizQuestion().catch((error) => {
          console.error("Error fetching quiz question:", error);
          onQuizNotFound();
        });
      } else {
        setMessages((prevMessages) => [
          ...prevMessages,
          {
            id: prevMessages.length + 1,
            message: botGreetingMsg || "Hello, how can I help you today?",
            sender: MessageSender.Bot,
            docs: [],
            imagesLoading: false,
          },
        ]);
      }
    }
  }, [
    configLoaded,
    botGreetingMsg,
    messagesLength,
    queryType,
    queryTypeFromConfig,
    api,
    orgSlug,
    subOrgSlug,
    suborgConfigLoaded,
    quizId,
    navigate,
    queryTypeRef,
    setMessages,
    onQuizNotFound,
  ]);
};
